import React, { forwardRef } from "react"
import { Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"

import "./menuBar.css"

interface ToolbarIconButtonProps {
  /** Icon shown on the button */
  icon?: IconDefinition
  /** Content instead of an icon (e.g. the "(Q)" mark) */
  children?: React.ReactNode
  /** Tooltip and accessible name */
  title: string
  onClick?: () => void
  /** A toggle that is on: brand colour */
  active?: boolean
  disabled?: boolean
  className?: string
}

/**
 * A borderless square button for the Editor's toolbar (the shared quiet
 * button, styles/buttonTokens.css): grey on hover, brand colour when it is a
 * toggle that is on. The tooltip is its name.
 */
export const ToolbarIconButton = forwardRef<
  HTMLButtonElement,
  ToolbarIconButtonProps
>(({ icon, children, title, onClick, active = false, disabled = false, className = "" }, ref) => (
  <Tooltip title={title} placement="bottom">
    {/* A disabled button gets no mouse events, so the tooltip (e.g.
        "Formatting not allowed in labels") listens on this wrapper. */}
    <span className="inline-flex">
      <button
        ref={ref}
        type="button"
        aria-label={title}
        aria-pressed={active || undefined}
        onClick={onClick}
        disabled={disabled}
        className={`dq-btn dq-btn--icon dq-btn--quiet ${className}`.trim()}
      >
        {icon ? <FontAwesomeIcon icon={icon} /> : children}
      </button>
    </span>
  </Tooltip>
))
ToolbarIconButton.displayName = "ToolbarIconButton"

interface ToolbarTextButtonProps {
  icon: IconDefinition
  label: string
  onClick?: () => void
  disabled?: boolean
}

/**
 * A small quiet button with an icon and a name for the toolbar's top row
 * (Translations, Metadata). When the toolbar row is narrow (Preview open,
 * small screens) only the icon shows; the name stays as the tooltip and
 * accessible name (menuBar.css, container query on the row).
 */
export const ToolbarTextButton = forwardRef<
  HTMLButtonElement,
  ToolbarTextButtonProps
>(({ icon, label, onClick, disabled = false }, ref) => (
  <Tooltip title={label} placement="bottom">
    <button
      ref={ref}
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="dq-btn dq-btn--quiet"
    >
      <FontAwesomeIcon icon={icon} />
      <span className="toolbar-text-label">{label}</span>
    </button>
  </Tooltip>
))
ToolbarTextButton.displayName = "ToolbarTextButton"
